import React from 'react'
import {PageRouter} from './page-router'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {Provider} from 'react-redux'
import {store} from './redux/store'
import * as Sentry from '@sentry/react'
import {BrowserTracing} from '@sentry/tracing'
import {InitDataDogRum, DataDogStartSession} from './dataDogConfig'
import {loadCountryRegistry} from './utils/country-registry'

Sentry.init({
  integrations: [
    new BrowserTracing({
      // Without this, BrowserTracing injects a `sentry-trace` header into
      // every outgoing fetch/XHR, including third-party intake calls (e.g.
      // Datadog RUM, Recurly). Those endpoints don't allow that header in
      // their CORS policy, so the preflight fails and the request never
      // sends. Scope trace propagation to our own API hosts only.
      tracePropagationTargets: [/^\//, /(^|\.)thryv\.com$/, 'localhost:10000'],
    }),
  ],
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.REACT_APP_ENV,
  tracesSampleRate: 1.0,
})

if (process.env.NODE_ENV !== 'development') {
  // No Auth0 identity in this app — clients arrive via a signed LinkId,
  // so that's the closest thing we have to a user identifier for RUM.
  const linkId = new URLSearchParams(window.location.search).get('LinkId')
  InitDataDogRum({name: linkId || 'anonymous', email: ''})
  DataDogStartSession()
}

const FullPageErrorFallback = ({error, resetError}) => (
  <div style={{padding: '40px', textAlign: 'center'}}>
    <h1>Something went wrong</h1>
    <p>{error.message}</p>
    <button onClick={resetError}>Try again</button>
  </div>
)

// The payment forms look the customer's country up in COUNTRIES on first render,
// so wait for the backend country registry (or its fallback) before routing.
function CountryRegistryGate({children}) {
  const [ready, setReady] = React.useState(false)
  React.useEffect(() => {
    loadCountryRegistry().finally(() => setReady(true))
  }, [])
  return ready ? children : null
}

function App() {
  return (
    <>
      <Sentry.ErrorBoundary fallback={FullPageErrorFallback}>
        <Provider store={store}>
          <CountryRegistryGate>
            <PageRouter />
          </CountryRegistryGate>
          <div className="container mt-4">
            <ToastContainer />
          </div>
        </Provider>
      </Sentry.ErrorBoundary>
    </>
  )
}

export default App
